import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reloadIfStaleChunk } from "@/utils/lazyWithRetry";

function errorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return error.statusText || String(error.data) || "Something went wrong";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}

/**
 * Rendered by React Router `errorElement` when a route throws during render
 * or when a loader/action fails without handling.
 */
export default function RouteErrorFallback() {
  const error = useRouteError();
  const message = errorMessage(error);
  const isDev = import.meta.env.DEV;

  // If we landed here because the user's tab has a stale build referencing
  // chunks that no longer exist on the CDN, auto-reload once to pick up the
  // new index.html. Session-guarded so we never loop.
  useEffect(() => {
    reloadIfStaleChunk(error);
  }, [error]);

  const reload = () => window.location.reload();

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-[#e6e8e7] bg-[#f7f9f8] shadow-sm p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#fde7e9] text-[#c41e3a]">
          <AlertTriangle className="h-7 w-7" strokeWidth={1.75} />
        </div>
        <h1 className="text-lg font-semibold text-[#1e302a] tracking-tight">
          Something went wrong
        </h1>
        <p className="mt-2 text-sm text-[#697571] leading-relaxed">
          This page hit an error and could not be displayed. You can try again
          or return to your workspace.
        </p>
        <p className="mt-4 rounded-lg bg-white/80 px-3 py-2 text-left text-xs font-mono text-[#374741] break-words border border-[#e6e8e7]">
          {message}
        </p>
        {isDev && error instanceof Error && error.stack && (
          <pre className="mt-3 max-h-40 overflow-auto rounded-lg border border-[#e6e8e7] bg-[#1e1e1e] p-3 text-left text-[10px] text-[#d4d4d4] whitespace-pre-wrap break-all">
            {error.stack}
          </pre>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            type="button"
            variant="outline"
            className="border-[#c1e1d5] text-[#1e302a] hover:bg-[#c1e1d5]/40"
            onClick={reload}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload page
          </Button>
          <Button
            type="button"
            className="bg-[#218760] hover:bg-[#1a6d4d] text-white"
            asChild
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
